'use client';

import {
	Button,
	For,
	HStack,
	IconButton,
	SimpleGrid,
	Stack,
	Text,
} from '@chakra-ui/react';
import { MdDeleteOutline } from 'react-icons/md';

import type { DesiredJobOccupationStep } from '@/modules/authentication/view-model';
import {
	useFieldArrayList, useOccupationCollections, useOccupationFormWatch,
} from '@/modules/shared';

import { Form } from '@packages/hook-form-wrapper';

type PreferredJobOccupationChoiceProps = {
	name:          `preferredJobOccupations.${number}`
	title:         string
	onClickRemove: () => void
};

export const PreferredJobOccupationChoice = ({
	name,
	onClickRemove,
	title,
}: PreferredJobOccupationChoiceProps) => {
	const {
		categoryId,
		subCategoryId,

		category,
		specialization,
		subCategory,
	} = useOccupationFormWatch<DesiredJobOccupationStep>(name as never);
	const {
		categoryCollection,
		specializationCollection,
		subCategoryCollection,
	} = useOccupationCollections(categoryId, subCategoryId);

	return (
		<Stack gap="sm">
			<HStack justifyContent="space-between">
				<Text textStyle="bodyBold">
					{title}
				</Text>
				<IconButton
					aria-label="remove"
					onClick={onClickRemove}
					size="md"
					variant="plain"
				>
					<MdDeleteOutline />
				</IconButton>
			</HStack>
			<SimpleGrid columns={2} gap="sm">
				<Form.SelectInput
					collection={categoryCollection}
					label="Category"
					name={category.name}
					placeholder="Category"
				/>
				<Form.SelectInput
					collection={subCategoryCollection}
					disabled={subCategory.disabled}
					label="Subcategory"
					name={subCategory.name}
					placeholder="Subcategory"
				/>
				<Form.SelectInput
					collection={specializationCollection}
					disabled={specialization.disabled}
					label="Specialization"
					name={specialization.name}
					placeholder="Specialization"
				/>
			</SimpleGrid>
		</Stack>
	);
};

export const DesiredJobOccupation = () => {
	const {
		add, canAddMore, fields, remove,
	} = useFieldArrayList<DesiredJobOccupationStep>({
		defaultRow: {},
		max:        3,
		name:       'preferredJobOccupations',
	});

	return (
		<Stack gap="md">
			<For each={fields} fallback={null}>
				{(field, index) => (
					<PreferredJobOccupationChoice
						key={field.id}
						name={`preferredJobOccupations.${index}`}
						onClickRemove={() => remove(index)}
						title={`Occupation ${index + 1}`}
					/>
				)}
			</For>
			<Stack gap="sm">
				<Button disabled={!canAddMore} onClick={add} variant="outline">
					Add Occupation
				</Button>
				<Text color="gray.100" textAlign="center" textStyle="caption">
					Up to 3 can be registered
				</Text>
			</Stack>
		</Stack>
	);
};
